/**
 * SSNavigation — Navigation Tree Admin (§6.9)
 *
 * Covers:
 *   GET /navigation/tree  — permission-filtered menu tree for current user
 *   Navigation items CRUD (admin)
 *   Navigation structures CRUD (admin)
 *   Favorites (add / remove)
 *   History (view / record)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ssNavigationAPI } from '../../services/ssApi';
import { useAuth } from '../../context/AuthContext';
import {
  Page, PageHeader, Card, Modal, TabBar, Section,
  Button, Badge, Field, Input, Select, Textarea,
  Spinner, EmptyState, ErrorBanner, SuccessBanner, InfoBanner,
  DataTable, InfoGrid, SearchBar,
  COLOR, FONT, RADIUS,
} from '../../components/ss';

// ── Nav Item form ─────────────────────────────────────────────────────────────
function NavItemForm({ open, initial, onClose, onDone }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({ name: '', route: '', icon: '', description: '', securitymodule: '', securityobject: '', securityobjectmethod: '', displayorder: 0 });
  const [saving, setSaving] = useState(false);
  const [err, setErr]   = useState('');
  const upd = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (initial) setForm({ name: initial.name || '', route: initial.route || '', icon: initial.icon || '', description: initial.description || '', securitymodule: initial.securitymodule || '', securityobject: initial.securityobject || '', securityobjectmethod: initial.securityobjectmethod || '', displayorder: initial.displayorder || 0 });
    else setForm({ name: '', route: '', icon: '', description: '', securitymodule: '', securityobject: '', securityobjectmethod: '', displayorder: 0 });
    setErr('');
  }, [initial, open]);

  async function submit(e) {
    e.preventDefault();
    if (!form.name) { setErr('Name is required.'); return; }
    setSaving(true); setErr('');
    try {
      if (isEdit) await ssNavigationAPI.updateItem(initial.navigationitemid || initial.id, form);
      else        await ssNavigationAPI.createItem(form);
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal open={open} title={isEdit ? 'Edit Nav Item' : 'New Nav Item'} onClose={onClose} width={540}>
      <ErrorBanner>{err}</ErrorBanner>
      <form onSubmit={submit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Label" required style={{ marginBottom: 12 }}><Input required value={form.name} onChange={upd('name')} placeholder="e.g. Patients" /></Field>
          <Field label="Icon (emoji)" style={{ marginBottom: 12 }}><Input value={form.icon} onChange={upd('icon')} placeholder="e.g. 🏥" /></Field>
          <Field label="Route" style={{ gridColumn: '1/-1', marginBottom: 12 }}><Input value={form.route} onChange={upd('route')} placeholder="e.g. /patients" /></Field>
          <Field label="Description" style={{ gridColumn: '1/-1', marginBottom: 12 }}><Input value={form.description} onChange={upd('description')} placeholder="Short description" /></Field>
          <Field label="Security Module" style={{ marginBottom: 12 }}><Input value={form.securitymodule} onChange={upd('securitymodule')} placeholder="e.g. patients" /></Field>
          <Field label="Security Object" style={{ marginBottom: 12 }}><Input value={form.securityobject} onChange={upd('securityobject')} placeholder="e.g. Record" /></Field>
          <Field label="Security Method" style={{ marginBottom: 12 }}><Input value={form.securityobjectmethod} onChange={upd('securityobjectmethod')} placeholder="e.g. List" /></Field>
          <Field label="Display Order" style={{ marginBottom: 18 }}><Input type="number" value={form.displayorder} onChange={upd('displayorder')} /></Field>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? 'Saving…' : isEdit ? 'Save' : 'Create Item'}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Nav Tree renderer ─────────────────────────────────────────────────────────
function NavTreeNode({ node, depth = 0 }) {
  const [open, setOpen] = useState(depth < 2);
  const hasChildren = node.children && node.children.length > 0;
  const label = node.name || node.item?.name || node.label || `Node #${node.navigationstructureid || node.id}`;
  const icon  = node.item?.icon || node.icon || (hasChildren ? '📁' : '📄');
  const route = node.item?.route || node.url || null;

  return (
    <div>
      <div
        onClick={() => hasChildren && setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '7px 10px',
          paddingLeft: 10 + depth * 18,
          borderRadius: RADIUS.md, marginBottom: 2, cursor: hasChildren ? 'pointer' : 'default',
          fontSize: FONT.size.base, color: COLOR.text,
          background: depth === 0 ? COLOR.graySubtle : 'transparent',
        }}
      >
        {hasChildren && <span style={{ fontSize: 10, color: COLOR.gray, width: 12 }}>{open ? '▾' : '▸'}</span>}
        {!hasChildren && <span style={{ width: 12 }} />}
        <span style={{ fontSize: 16 }}>{icon}</span>
        <span style={{ fontWeight: depth === 0 ? 600 : 400 }}>{label}</span>
        {route && <span style={{ fontSize: 11, color: COLOR.grayLight, marginLeft: 'auto', fontFamily: 'monospace' }}>{route}</span>}
      </div>
      {hasChildren && open && node.children.map((child, i) => (
        <NavTreeNode key={child.id || child.navigationitemid || i} node={child} depth={depth + 1} />
      ))}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function SSNavigation() {
  const { user } = useAuth();
  // Backend favorites/history endpoints require a numeric SS user id as
  // `user_id`. The auth payload stores party_id/name/roles — accept any
  // numeric id field we can find, otherwise hide those affordances.
  const _rawUserId = user?.user_id ?? user?.userid ?? user?.ssuserid ?? user?.id ?? user?.party_id;
  const ssUserId = (_rawUserId !== null && _rawUserId !== undefined && _rawUserId !== '' && Number.isInteger(Number(_rawUserId)))
    ? Number(_rawUserId) : null;

  const [tab, setTab]             = useState('tree');
  const [tree, setTree]           = useState([]);
  const [items, setItems]         = useState([]);
  const [structures, setStructures] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [history, setHistory]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [err, setErr]             = useState('');
  const [ok, setOk]               = useState('');
  const [showItemForm, setShowItemForm]   = useState(false);
  const [editItem, setEditItem]           = useState(null);
  const [search, setSearch]               = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      if (tab === 'tree')       { const d = await ssNavigationAPI.tree();           setTree(d.data || d || []); }
      if (tab === 'items')      { const d = await ssNavigationAPI.listItems();      setItems(d.data || d || []); }
      if (tab === 'structures') { const d = await ssNavigationAPI.listStructures(); setStructures(d.data || d || []); }
      if (tab === 'favorites')  { const d = await ssNavigationAPI.listStructures(); setFavorites(d.data || d || []); }
      if (tab === 'history')    {
        if (ssUserId == null) { setHistory([]); }
        else { const d = await ssNavigationAPI.getHistory(ssUserId); setHistory(d.data || d || []); }
      }
    } catch (ex) { setErr(ex.response?.data?.detail || ex.message || 'Load failed'); }
    finally { setLoading(false); }
  }, [tab, ssUserId]);

  useEffect(() => { load(); }, [load]);

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete nav item "${item.name || item.itemlabel}"?`)) return;
    try { await ssNavigationAPI.deleteItem(item.navigationitemid || item.id); load(); }
    catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
  };

  const removeFavorite = async (structureId) => {
    if (ssUserId == null) return;
    try { await ssNavigationAPI.removeFavorite(structureId, ssUserId); setOk('Favorite removed'); load(); }
    catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
  };

  const filteredItems = items.filter(i => !search || (i.name || i.itemlabel || '').toLowerCase().includes(search.toLowerCase()));

  const ITEM_COLS = [
    { key: 'icon', label: '', render: v => <span style={{ fontSize: 18 }}>{v || '📄'}</span> },
    { key: 'name', label: 'Label', render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'route', label: 'Route', render: v => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v || '—'}</span> },
    { key: 'securitymodule', label: 'Permission', render: (v, r) => v ? <span style={{ fontFamily: 'monospace', fontSize: 11, color: COLOR.gray }}>{v}.{r.securityobject}.{r.securityobjectmethod}</span> : '—' },
    { key: '_actions', label: '', render: (_, item) => (
      <div style={{ display: 'flex', gap: 4 }}>
        <Button variant="ghost" size="sm" onClick={() => setEditItem(item)}>Edit</Button>
        <Button variant="danger" size="sm" onClick={() => deleteItem(item)}>✕</Button>
      </div>
    )},
  ];

  const STRUCT_COLS = [
    { key: 'name',         label: 'Structure', render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'displayorder', label: 'Order' },
    { key: '_fav', label: '', render: (_, s) => (
      ssUserId == null ? null : (
        <Button variant="ghost" size="sm" onClick={async () => { try { await ssNavigationAPI.addFavorite(s.navigationstructureid || s.id, ssUserId); setOk('Added to favorites'); } catch (ex) { setErr(ex.response?.data?.detail || ex.message); } }}>
          ⭐ Favorite
        </Button>
      )
    )},
  ];

  const HISTORY_COLS = [
    { key: 'structure_name', label: 'Page',    render: v => <span style={{ fontWeight: 600 }}>{v || '—'}</span> },
    { key: 'navigationstructureid', label: 'Structure ID', render: v => <span style={{ color: COLOR.grayLight, fontFamily: 'monospace' }}>#{v}</span> },
    { key: 'visitedat',      label: 'Visited', render: v => v ? new Date(v).toLocaleString() : '—' },
  ];

  const TABS = [
    { id: 'tree',       label: '🌲 Live Tree' },
    { id: 'items',      label: '📄 Items' },
    { id: 'structures', label: '📁 Structures' },
    // Favorites/history require a numeric SS user id — hide when unavailable.
    ...(ssUserId != null ? [
      { id: 'favorites',  label: '⭐ Favorites' },
      { id: 'history',    label: '🕑 History' },
    ] : []),
  ];

  return (
    <Page>
      <PageHeader
        title="Navigation"
        subtitle="Manage navigation tree, items, structures, favorites and history (§6.9)"
        action={tab === 'items' && <Button onClick={() => setShowItemForm(true)}>+ New Item</Button>}
      />

      <ErrorBanner>{err}</ErrorBanner>
      <SuccessBanner>{ok}</SuccessBanner>

      <TabBar tabs={TABS} active={tab} onChange={t => { setErr(''); setOk(''); setTab(t); }} style={{ marginBottom: 20 }} />

      {/* Live Tree */}
      {tab === 'tree' && (
        <Card>
          <InfoBanner>This is the permission-filtered navigation tree as seen by the current user. Items they cannot access are excluded.</InfoBanner>
          {loading ? <Spinner /> : (
            Array.isArray(tree) && tree.length === 0
              ? <EmptyState icon="🌲" title="Navigation tree is empty" message="Add items and structures, then use Rescan to generate the tree." />
              : (
                <div>
                  {(Array.isArray(tree) ? tree : []).map((node, i) => (
                    <NavTreeNode key={node.id || node.navigationstructureid || i} node={node} />
                  ))}
                </div>
              )
          )}
        </Card>
      )}

      {/* Items */}
      {tab === 'items' && (
        <Card>
          <SearchBar value={search} onChange={setSearch} placeholder="Search items…" style={{ marginBottom: 14 }} />
          <DataTable
            columns={ITEM_COLS}
            rows={filteredItems}
            rowKey={r => r.navigationitemid || r.id || Math.random()}
            loading={loading}
            emptyIcon="🕑"
            emptyTitle="No navigation history"
          />
        </Card>
      )}

      <NavItemForm open={showItemForm} initial={null} onClose={() => setShowItemForm(false)} onDone={() => { setShowItemForm(false); load(); }} />
      <NavItemForm open={!!editItem} initial={editItem} onClose={() => setEditItem(null)} onDone={() => { setEditItem(null); load(); }} />
    </Page>
  );
}
